import type {
  ActiveCookieEvidence,
  LatestDanmakuEvidence,
} from './api';
import type { LiveSession, SentimentAnalysisResult } from '@/types';

export type MonitorTone = 'healthy' | 'warning' | 'live' | 'error' | 'unknown' | 'action';

export interface MonitorSegmentState {
  label: string;
  detail: string;
  tone: MonitorTone;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export function toEpochMilliseconds(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined) return null;

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;
    return value < 1_000_000_000_000 ? value * 1000 : value;
  }

  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? null : parsed;
}

export function formatRelativeEvidenceTime(
  value: string | number | null | undefined,
  now = Date.now()
): string {
  const timestamp = toEpochMilliseconds(value);
  if (timestamp === null) return '时间未知';

  const age = Math.max(0, now - timestamp);
  if (age < MINUTE) return '刚刚';
  if (age < HOUR) return `${Math.floor(age / MINUTE)} 分钟前`;
  if (age < DAY) return `${Math.floor(age / HOUR)} 小时前`;
  return `${Math.floor(age / DAY)} 天前`;
}

export function getLoginSegment(
  cookie: ActiveCookieEvidence | null,
  now = Date.now()
): MonitorSegmentState {
  if (!cookie) {
    return { label: '未配置', detail: '请在设置页导入 Cookie', tone: 'warning' };
  }

  const expiresAt = toEpochMilliseconds(cookie.expires_at);
  if (expiresAt !== null && expiresAt <= now) {
    return { label: '已过期', detail: '需要重新配置 Cookie', tone: 'error' };
  }

  const evidenceTime = cookie.last_validated_at ?? cookie.created_at;
  return {
    label: '已配置',
    detail: evidenceTime ? `最近记录 ${formatRelativeEvidenceTime(evidenceTime, now)}` : '存在激活配置',
    tone: 'healthy',
  };
}

export function getCaptureSegment(sessions: LiveSession[]): MonitorSegmentState {
  const liveSessions = sessions.filter((session) => session.status === 'live');
  const recordingCount = liveSessions.filter((session) => session.is_recording).length;

  if (recordingCount > 0) {
    return {
      label: '采集中',
      detail: `${recordingCount} 个场次正在录制`,
      tone: 'live',
    };
  }

  if (liveSessions.length > 0) {
    return {
      label: '有直播任务',
      detail: `${liveSessions.length} 个场次等待录制状态`,
      tone: 'warning',
    };
  }

  return { label: '待机', detail: '当前无直播任务', tone: 'unknown' };
}

function getLatestEvidenceTime(evidence: LatestDanmakuEvidence | null): string | number | null {
  return evidence?.timestamp ?? evidence?.created_at ?? null;
}

export function getDataSegment(
  activeSession: LiveSession | null,
  evidence: LatestDanmakuEvidence | null,
  now = Date.now()
): MonitorSegmentState {
  if (!activeSession) {
    return { label: '待机', detail: '当前无直播数据', tone: 'unknown' };
  }

  const latestTime = getLatestEvidenceTime(evidence);
  const latestMs = toEpochMilliseconds(latestTime);
  if (latestMs !== null) {
    const age = Math.max(0, now - latestMs);
    return {
      label: age <= 2 * MINUTE ? '接收中' : '已有数据',
      detail: `最近数据 ${formatRelativeEvidenceTime(latestTime, now)}`,
      tone: age <= 2 * MINUTE ? 'healthy' : 'warning',
    };
  }

  if (activeSession.danmaku_count > 0) {
    return {
      label: '已有数据',
      detail: `${activeSession.danmaku_count.toLocaleString()} 条，缺少最新时间`,
      tone: 'warning',
    };
  }

  return { label: '等待数据', detail: '尚未收到弹幕', tone: 'unknown' };
}

export function getDataHealthSegment(
  activeSession: LiveSession | null,
  evidence: LatestDanmakuEvidence | null,
  now = Date.now()
): MonitorSegmentState {
  if (!activeSession) {
    return { label: '待机', detail: '当前无直播', tone: 'unknown' };
  }

  const latestTime = getLatestEvidenceTime(evidence);
  const latestMs = toEpochMilliseconds(latestTime);
  if (latestMs === null) {
    return activeSession.danmaku_count > 0
      ? { label: '需观察', detail: '有累计数据，但缺少时间证据', tone: 'warning' }
      : { label: '等待样本', detail: '聊天室暂未产生弹幕', tone: 'unknown' };
  }

  const age = Math.max(0, now - latestMs);
  if (age <= 2 * MINUTE) {
    return { label: '良好', detail: '持续收到数据', tone: 'healthy' };
  }

  if (age <= 5 * MINUTE) {
    return {
      label: '需观察',
      detail: `最近数据 ${formatRelativeEvidenceTime(latestTime, now)}`,
      tone: 'warning',
    };
  }

  return {
    label: '需观察',
    detail: `聊天室可能静默 · ${formatRelativeEvidenceTime(latestTime, now)}`,
    tone: 'warning',
  };
}

export function getSentimentSegment(
  activeSession: LiveSession | null,
  analysis: SentimentAnalysisResult | null
): MonitorSegmentState {
  if (!activeSession) {
    return { label: '未运行', detail: '当前无直播', tone: 'unknown' };
  }

  if (!analysis) {
    return { label: '未运行', detail: '本场次尚未生成分析', tone: 'unknown' };
  }

  switch (analysis.status) {
    case 'pending':
      return { label: '等待分析', detail: '任务已创建', tone: 'unknown' };
    case 'processing':
      return { label: '分析中', detail: '正在处理本场弹幕', tone: 'action' };
    case 'completed':
      return {
        label: '已完成',
        detail: analysis.danmaku_count
          ? `已分析 ${analysis.danmaku_count.toLocaleString()} 条`
          : '本场分析已生成',
        tone: 'healthy',
      };
    case 'failed':
      return { label: '分析失败', detail: '最近一次分析未完成', tone: 'error' };
  }
}
